.select {
  position: relative;
  display: inline-block;
  width: 100%;

  &--disabled {
    pointer-events: none;

    .select__trigger {
      background-color: var(--color-neutral-neutral-lightest);
      border-color: var(--border-color-secondary);
    }

    .select__value {
      color: var(--text-color-tertiary);
    }
  }

  &__trigger {
    width: 100%;
    padding: var(--spacing-sm);
    font-size: map-get($font-sizes, md);
    color: var(--text-color-primary);
    border: var(--border-size-sm) solid var(--border-color-primary);
    background-color: var(--color-neutral-white);
    border-radius: var(--radius-size-md);
    cursor: pointer;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s linear;
    user-select: none;

    &:hover {
      box-shadow: 0 0 0 1px rgb(0 0 0 / 100%);
    }

    &:focus {
      box-shadow:
        0 0 0 var(--border-size-md) var(--color-neutral-white),
        0 0 0 var(--border-size-lg) var(--color-brand-primary-light);
      outline: none;
    }

    &:hover:focus {
      box-shadow:
        0 0 0 var(--border-size-md) var(--color-neutral-white),
        0 0 0 var(--border-size-lg) var(--color-brand-primary-light);
    }
  }

  &__value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--placeholder {
      color: var(--text-color-secondary);
    }
  }

  &__arrow {
    @include size(24px);

    margin-left: var(--spacing-xs);
    position: relative;
    flex-shrink: 0;
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    svg {
      display: block;
      width: 100%;
      height: 100%;
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      transform: rotate(0deg);
    }
  }

  &__dropdown {
    position: absolute;
    left: 0;
    top: 90px;
    width: 100%;
    background: var(--color-neutral-white);
    border-radius: var(--radius-size-md);
    box-shadow:
      0 4px 8px -2px rgb(0 0 0 / 10%),
      0 2px 4px -2px rgb(0 0 0 / 6%);
    padding: 0;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 0.2s ease,
      visibility 0s linear 0.2s,
      transform 0.2s ease;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--color-neutral-neutral-lightest) var(--color-neutral-white);

    &::-webkit-scrollbar {
      width: var(--spacing-xs);
      background: var(--color-neutral-white);
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-neutral-neutral-lightest);
      border-radius: var(--radius-size-md);
    }
  }

  &__option {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    height: 48px;
    padding: 0 var(--spacing-sm);
    font-size: map-get($font-sizes, md);
    font-weight: map-get($font-weights, medium);
    color: var(--text-color-primary);
    background: transparent;
    border-radius: 0;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
    outline: none;
    position: relative;
    min-width: 0;

    &:hover,
    &--focused {
      background: var(--color-neutral-neutral-lightest);
    }

    &--selected {
      .select__option-check {
        display: block;
      }
    }

    .select__option-check {
      margin-left: auto;

      @include size(20px);

      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .select__option-label {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      max-width: 100%;
    }
  }

  &__native {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &--open {
    z-index: 1001;

    .select__arrow {
      transform: none;

      svg {
        transform: rotate(-180deg);
      }
    }

    .select__trigger {
      box-shadow: 0 0 0 1px rgb(0 0 0 / 100%);

      &:focus {
        box-shadow:
          0 0 0 var(--border-size-md) var(--color-neutral-white),
          0 0 0 var(--border-size-lg) var(--color-brand-primary-light);
        outline: none;
      }
    }

    .select__dropdown {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition-delay: 0s;
    }

    &.select--top {
      .select__arrow {
        &::before {
          transform: rotate(225deg);
        }

        &::after {
          transform: rotate(135deg);
        }
      }

      .select__dropdown {
        top: auto;
        bottom: 100%;
        margin-bottom: var(--spacing-2xs);
        transform: translateY(0);
      }
    }

    &.select--bottom {
      .select__arrow {
        &::before {
          transform: rotate(-45deg);
        }

        &::after {
          transform: rotate(45deg);
        }
      }

      .select__dropdown {
        top: 100%;
        margin-top: var(--spacing-2xs);
        transform: translateY(0);
      }
    }
  }

  &--top:not(.select--open) .select__dropdown {
    top: auto;
    bottom: 100%;
    transform: translateY(10px);
  }

  &--bottom:not(.select--open) .select__dropdown {
    top: 100%;
    transform: translateY(-10px);
  }

  &__field {
    width: 100%;
    padding: var(--spacing-sm);
    font-family: inherit;
    font-size: map-get($font-sizes, md);
    line-height: 1.5;
    color: var(--text-color-primary);
    background-color: var(--background-color-primary);
    appearance: none;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &::placeholder {
      color: var(--text-color-secondary);
    }

    &:focus {
      outline: none;
    }

    &:disabled {
      background-color: var(--color-neutral-neutral-lightest);
      border: var(--border-size-sm) solid var(--border-color-secondary);
      cursor: not-allowed;
    }
  }
}

.select__trigger--error {
  border-color: var(--color-system-error);
}

.select__label {
  display: inline-flex;
  padding-bottom: var(--spacing-sm);
}
